import { FormInputProps } from '@/@types/form.types';
import { cn } from '@/lib/utils';
import { Controller } from 'react-hook-form';
import InformationCircle from '@/components/icons/InformationCircle';
import { useTranslation } from 'react-i18next';

type Props<T extends {}> = {
	label?: string;
	placeholder?: string;
	optional?: boolean;
	type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
} & FormInputProps<T>;

const ETextInput = <T extends {}>({
	label,
	placeholder = 'Enter remarks',
	optional = false,
	fieldName,
	control,
	className,
	containerClassName,
	disabled,
	type = 'text',
}: Props<T>) => {
	const { t } = useTranslation();

	return (
		<div
			className={cn(
				'w-full flex flex-col gap-1.5 select-none',
				containerClassName,
			)}>
			{label && (
				<h4 className='text-sm font-medium'>
					{label}
					{optional && (
						<span className='text-popover-600'> (optional)</span>
					)}
				</h4>
			)}
			<Controller
				name={fieldName}
				control={control}
				render={({
					field: { value, onChange, onBlur },
					fieldState,
					formState,
				}) => (
					<>
						<input
							type={type}
							value={value ?? ''}
							onChange={(e) =>
								onChange(
									type === 'number'
										? e.target.value === ''
											? null
											: +e.target.value
										: e.target.value,
								)
							}
							onBlur={onBlur}
							disabled={disabled || formState.disabled}
							placeholder={placeholder}
							className={cn(
								'p-3.5 border border-popover-100 rounded-semi-xl text-xs text-popover-800 bg-popover-10 focus:outline-hidden focus:border focus:border-primary-color-1000 placeholder:text-popover-600 disabled:bg-popover-50 transition-colors duration-150',
								fieldState.invalid ? 'border-error-1000' : '',
								className,
							)}
						/>
						{fieldState.invalid && (
							<div className='flex items-center gap-1.5'>
								<InformationCircle className='size-4 stroke-error-1000' />
								<span className='text-xs text-error-1000'>
									{t(fieldState.error?.message as string)}
								</span>
							</div>
						)}
					</>
				)}
			/>
		</div>
	);
};

export default ETextInput;
